import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Empty, Progress, Skeleton, Space, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, CheckCircleFilled, FilePdfOutlined, FileTextOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { groupTrainingMaterials } from '../utils/trainingDocuments';
import api from '../services/api';
import { TrainingFolder } from '../types';
import { formatTrainingDate } from '../utils/trainingDates';
import './VideoPortal.css';
import '../components/TrainingWorkspace.css';

const { Title, Paragraph, Text } = Typography;

const VideoFolder: React.FC = () => {
  const { folderId } = useParams();
  const navigate = useNavigate();
  const [folder, setFolder] = useState<TrainingFolder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(''); setFolder(null);
    api.get<TrainingFolder>(`/video/folders/${folderId}`, { signal: controller.signal })
      .then(({ data }) => { if (!controller.signal.aborted) setFolder(data); })
      .catch(err => { if (!controller.signal.aborted) setError(err?.response?.data?.detail || 'Не удалось загрузить папку'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [folderId]);

  return <Layout>
    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/videos')} className="video-back">К каталогу</Button>
    {error && <Alert type="error" showIcon message={error} />}
    {loading ? <Card><Skeleton active /></Card> : folder && <>
      <div className="video-page-head"><div>
        <Title level={2}>{folder.title}</Title>
        {folder.description && <Paragraph type="secondary">{folder.description}</Paragraph>}
        <Paragraph type="secondary">Видеозапись, презентация и текст каждого обучения открываются на одном экране.</Paragraph>
      </div><Progress type="circle" size={76} percent={Math.round(folder.progress_percent)} /></div>
      {!folder.videos.length && !folder.documents?.length && <Card><Empty description="В этой папке пока нет материалов" /></Card>}
      <Space direction="vertical" size={16} className="video-list">
        {groupTrainingMaterials(folder.videos, folder.documents || []).map(training => {
          const presentations = training.documents.filter(item => item.document_kind === 'presentation');
          const transcripts = training.documents.filter(item => item.document_kind === 'transcript');
          const singleVideo = training.videos.length === 1 ? training.videos[0] : undefined;
          const singleDocument = presentations.length === 1 ? presentations[0] : transcripts.length === 1 ? transcripts[0] : undefined;
          const openRoute = singleVideo ? `/videos/${singleVideo.id}` : singleDocument ? `/videos/documents/${singleDocument.id}` : undefined;
          return <Card key={training.key} className="document-training-card">
            <Space wrap><Title level={4}>{training.title}</Title>
              {training.isCompleted && <Tag icon={<CheckCircleFilled />} color="success">Изучено</Tag>}</Space>
            <Text type="secondary">Дата обучения: {formatTrainingDate(training.trainingDate)}</Text>
            <div className="training-session-files">
              {training.videos.map(video => <div key={`video-${video.id}`} className="training-session-file">
                <Button type="link" icon={<PlayCircleOutlined />} onClick={() => navigate(`/videos/${video.id}`)}>Видеозапись: {video.title}</Button>
                {video.progress?.is_completed && <Tag color="success">Просмотрено</Tag>}
              </div>)}
              {training.documents.map(document => <div key={`document-${document.id}`} className="training-session-file">
                <Button type="link" icon={document.document_kind === 'presentation' ? <FilePdfOutlined /> : <FileTextOutlined />} onClick={() => navigate(`/videos/documents/${document.id}`)}>
                  {document.document_kind === 'presentation' ? 'Презентация' : 'Текст'}: {document.title}
                </Button>
                {document.progress?.is_completed && <Tag color="success">Прочитано</Tag>}
              </div>)}
            </div>
            {openRoute ? <Button type="primary" className="training-session-open" onClick={() => navigate(openRoute)}>Открыть обучение</Button>
              : <Paragraph type="secondary">Выберите материал, с которого начать обучение.</Paragraph>}
          </Card>;
        })}
      </Space>
    </>}
  </Layout>;
};

export default VideoFolder;
